import { useEffect, useMemo, useState } from "react";
import RichTextEditor from "./RichTextEditor";

export const EMPTY_TICKET_FORM = {
  title: "",
  description: "",
  description_html: "",
  sender_email: "",
  sender_name: "",
  company_id: "",
  priority: "medium",
  technician_id: "",
  due_date: "",
  contact_id: "",
};

export const openingHtmlToPlainText = (html = "") => {
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(?:div|p|li)>/gi, "\n");
  const doc = new DOMParser().parseFromString(withBreaks, "text/html");
  return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
};

const contactName = (contact) => `${contact.first_name || ""} ${contact.last_name || ""}`.trim();

const contactHaystack = (contact) =>
  [contact.first_name, contact.last_name, contact.email, contact.phone, contact.company_name]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export default function TicketCreateForm({
  form,
  setForm,
  companies = [],
  contacts = [],
  technicians,
  priorityOptions,
  createMutation,
  onSubmit,
  onCancel,
}) {
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [descriptionError, setDescriptionError] = useState("");

  const filteredContacts = useMemo(() => {
    const search = contactSearch.trim().toLowerCase();
    return search ? contacts.filter((contact) => contactHaystack(contact).includes(search)) : contacts;
  }, [contactSearch, contacts]);

  // The contact-picker dialog had no Escape handler -- a keyboard user had to click either the
  // backdrop or the unlabeled ✕ to dismiss it.
  useEffect(() => {
    if (!showContactPicker) return;
    const onKeyDown = (e) => { if (e.key === "Escape") setShowContactPicker(false); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showContactPicker]);

  const selectContact = (contact) => {
    const matchedCompany = companies.find((company) =>
      company.id === contact.company_id ||
      (contact.company_name && company.name?.trim().toLowerCase() === contact.company_name.trim().toLowerCase())
    );
    setForm((f) => ({
      ...f,
      sender_name: contactName(contact) || f.sender_name,
      sender_email: contact.email || f.sender_email,
      company_id: matchedCompany?.id || f.company_id,
      contact_id: contact.id,
    }));
    setShowContactPicker(false);
    setContactSearch("");
  };

  return (
    <form onSubmit={onSubmit} className="bg-white rounded-xl shadow-sm border p-4 xl:p-5 2xl:p-6 space-y-4">
      <h2 className="font-semibold text-gray-700 text-lg">פתיחת קריאה חדשה</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <label className="block text-xs text-gray-500">שם הפונה</label>
            <button
              type="button"
              onClick={() => setShowContactPicker(true)}
              className="text-xs border rounded-lg px-2 py-1 text-brand-700 hover:bg-brand-50"
            >
              בחר איש קשר
            </button>
          </div>
          <input
            required
            placeholder="ישראל ישראלי"
            value={form.sender_name}
            // Editing away from what the contact picker set means this may no longer be that
            // contact: same rule tickets.py's own sender-update path applies (a changed sender
            // without a fresh pick drops the association), applied here at create time instead.
            onChange={(e) => setForm((f) => ({ ...f, sender_name: e.target.value, contact_id: "" }))}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">מייל הפונה</label>
          <input
            required
            type="email"
            placeholder="user@company.co.il"
            value={form.sender_email}
            onChange={(e) => setForm((f) => ({ ...f, sender_email: e.target.value, contact_id: "" }))}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">חברה</label>
        <select
          value={form.company_id || ""}
          onChange={(e) => setForm((f) => ({ ...f, company_id: e.target.value }))}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        >
          <option value="">זיהוי אוטומטי לפי דומיין המייל</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>{company.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">כותרת</label>
        <input
          required
          placeholder="תיאור קצר של הבעיה"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">תיאור מפורט</label>
        <RichTextEditor
          value={form.description_html || ""}
          placeholder="פרט את הבעיה..."
          minRows={4}
          allowImages
          allowFormatting
          onError={setDescriptionError}
          onChange={(descriptionHtml) => {
            setDescriptionError("");
            setForm((f) => ({
              ...f,
              description_html: descriptionHtml,
              description: openingHtmlToPlainText(descriptionHtml),
            }));
          }}
        />
        {descriptionError && <p className="text-xs text-red-600 mt-1">{descriptionError}</p>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">עדיפות</label>
          <select
            value={form.priority}
            onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          >
            {priorityOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">שיבוץ טכנאי (אופציונלי)</label>
          <select
            value={form.technician_id}
            onChange={(e) => setForm((f) => ({ ...f, technician_id: e.target.value }))}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">ללא שיבוץ</option>
            {technicians.map((t) => (
              <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">מועד יעד לביצוע (אופציונלי)</label>
        <input type="date" value={form.due_date || ""} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
      </div>
      {createMutation.isError && (
        <p className="text-red-600 text-sm">{createMutation.error?.response?.data?.detail}</p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={createMutation.isPending}
          className="bg-brand-600 hover:bg-brand-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          {createMutation.isPending ? "שולח..." : "פתח קריאה"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="border px-5 py-2 rounded-lg text-sm hover:bg-gray-50"
        >
          ביטול
        </button>
      </div>

      {showContactPicker && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4" onClick={() => setShowContactPicker(false)}>
          <div role="dialog" aria-modal="true" aria-labelledby="contact-picker-title" className="bg-white rounded-xl shadow-xl border w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between gap-3">
              <h3 id="contact-picker-title" className="font-semibold text-gray-800">בחירת איש קשר</h3>
              <button type="button" onClick={() => setShowContactPicker(false)} aria-label="סגור" className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-4 border-b">
              <input
                autoFocus
                value={contactSearch}
                // The picker sits inside the create <form>: Enter in the search box submitted it and CREATED the ticket
                // (without the contact that was being searched for).
                onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                onChange={(e) => setContactSearch(e.target.value)}
                placeholder="חיפוש לפי שם, מייל, טלפון או חברה..."
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="overflow-y-auto max-h-[55vh]">
              {filteredContacts.length === 0 ? (
                <p className="p-4 text-sm text-gray-400">לא נמצאו אנשי קשר.</p>
              ) : (
                filteredContacts.map((contact) => (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => selectContact(contact)}
                    className="w-full text-right px-4 py-3 border-b hover:bg-gray-50"
                  >
                    <div className="font-medium text-gray-800">{contactName(contact)}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {[contact.email, contact.phone, contact.company_name].filter(Boolean).join(" · ")}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
